// Phase 6.AK — Lifecycle telemetry emitter (shared)
// ---------------------------------------------------------------------------
// Single source of truth for posting frontend lifecycle events to the
// backend. Used by every component that needs to record a step in the
// "did the user actually reach the analyze pipeline?" decision tree.
//
// Design goals:
//   1. **Reliability**: use `fetch` with `keepalive: true` so events
//      survive a page navigation in-flight. Fall back to
//      `navigator.sendBeacon` if `fetch` throws synchronously.
//   2. **Decoupling**: callers never await; this is fire-and-forget.
//      Telemetry must never block the UX.
//   3. **Correlation**: every emit accepts an optional
//      `client_trace_id` so consumers can group together a sequence
//      of events (route_entry → fetch_started → response_received
//      → render_completed) even when the backend hasn't issued a
//      request_id yet.
//   4. **Cross-cut context**: every event automatically receives
//      window.location.href, pathname, user_agent, build_phase_seen.
//      Callers only supply the things specific to the event itself.
//
// WS-4 STAGING FIX (2026-08-06) — resolve the backend origin through
// the CANONICAL resolver in `@/lib/api` (which prefers
// `window.__STOCKAILY_RUNTIME_CONFIG__.backendUrl` and falls back to
// `process.env.REACT_APP_BACKEND_URL`).  Reading `process.env`
// directly here silently produced empty-origin URLs in the Scaleway
// build (CRA/DefinePlugin substitution edge-case), causing every
// lifecycle POST to hit the FRONTEND origin (`staging.stockaily.ai`)
// and be rejected 405 by the nginx SPA-fallback.  Routing through
// `API` guarantees the same origin as every other `/api/*` call.
import { API } from "@/lib/api";

const LIFECYCLE_URL = `${API}/_health/frontend_lifecycle`;

// Generate a short uuid-ish trace id without pulling in a uuid dependency.
// 12 hex chars = ~48 bits of entropy. Sufficient for grouping events
// within a single browser session.
export function newClientTraceId() {
  try {
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const buf = new Uint8Array(6);
      crypto.getRandomValues(buf);
      return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch { /* fall through */ }
  // Fallback: less random but always works
  return (Date.now().toString(16) + Math.random().toString(16).slice(2, 10)).slice(0, 12);
}

/**
 * Emit a frontend lifecycle event.
 *
 * @param {string} event         Event name (snake_case). Examples:
 *                               route_entry, route_entry_blocked,
 *                               fetch_started, response_received,
 *                               response_accepted, timeout_screen_displayed,
 *                               frontend_timeout_no_backend_trace,
 *                               search_submit, search_normalized.
 * @param {object} payload       Free-form fields. Common useful keys:
 *                               ticker, request_id, client_trace_id,
 *                               user_email, reason, elapsed_ms, extra.
 */
export function emitLifecycle(event, payload = {}) {
  try {
    const body = {
      event,
      ticker: payload.ticker || null,
      request_id: payload.request_id || null,
      elapsed_ms: payload.elapsed_ms ?? null,
      url: (typeof window !== "undefined") ? window.location.href : null,
      user_agent: (typeof navigator !== "undefined") ? navigator.userAgent : null,
      user_email: payload.user_email || null,
      build_phase_seen: (typeof window !== "undefined" && window.__STOCKAILY_BUILD_PHASE__) || null,
      // Phase 6.AY — Render-confirmation fields. Optional on most events;
      // populated by Analyze.js when the analysis component mounts so the
      // Owner Console "Last Rendered Analysis" card has a single,
      // canonical source for provenance details that the user actually saw.
      analysis_id: payload.analysis_id || null,
      data_source: payload.data_source || null,
      cache_hit: payload.cache_hit ?? null,
      mock_used: payload.mock_used ?? null,
      fallback_used: payload.fallback_used ?? null,
      endpoint_coverage_pct: payload.endpoint_coverage_pct ?? null,
      chart_data_status: payload.chart_data_status || null,
      chart_points_count: payload.chart_points_count ?? null,
      route: payload.route || (typeof window !== "undefined" ? window.location.pathname : null),
      rendered_at: payload.rendered_at || null,
      extra: {
        // Always include a few cross-cutting fingerprint fields so the
        // operator can reconstruct context even from a single row.
        pathname: (typeof window !== "undefined") ? window.location.pathname : null,
        client_trace_id: payload.client_trace_id || null,
        ...(payload.extra || {}),
      },
    };
    // Strip any keys the consumer hoisted into payload (avoid duplicating
    // them inside extra).
    const json = JSON.stringify(body);
    // Primary path: fetch with keepalive so the request survives a
    // navigation away from the current page. This is critical for
    // capturing route_entry events that happen immediately before a
    // subsequent navigation kicks in.
    try {
      fetch(LIFECYCLE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: json,
        keepalive: true,
      }).catch(() => {
        // Last-resort fallback: sendBeacon. Cannot set Content-Type
        // application/json on a string Blob without triggering CORS
        // preflight, so we use the text/plain trick — the backend's
        // FastAPI route ignores Content-Type because it reads the
        // raw JSON body via the Pydantic model.
        try {
          if (typeof navigator !== "undefined" && navigator.sendBeacon) {
            const blob = new Blob([json], { type: "text/plain" });
            navigator.sendBeacon(LIFECYCLE_URL, blob);
          }
        } catch { /* swallow */ }
      });
    } catch {
      try {
        if (typeof navigator !== "undefined" && navigator.sendBeacon) {
          const blob = new Blob([json], { type: "text/plain" });
          navigator.sendBeacon(LIFECYCLE_URL, blob);
        }
      } catch { /* swallow */ }
    }
  } catch {
    /* never let telemetry break the UX */
  }
}
